/**
 * RegionChip — visible indicator of which regional silo the admin is operating on.
 *
 * Single-region mode: static badge with the backend's `REGION_CODE`
 * (sourced from `/api/auth/me`, so `dev` containers visibly differ from
 * `eu` / `us` production silos).
 *
 * Multi-region mode: interactive DropdownSelect; changing the region
 * rewrites the API client base URL via ``RegionContext`` so the same UI
 * starts hitting the other region's backend on the next call.
 */
import React from 'react';
import { DropdownSelect } from '@shared';
import { useRegion } from '@/contexts/RegionContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Static badge styling per region — dev is amber so it stands out, prod
 * silos use the neutral slate look so they don't compete for attention.
 */
const STATIC_BADGE_STYLES: Record<string, string> = {
    dev: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    eu: 'border-border-subtle dark:border-border-subtle-dark bg-slate-50 dark:bg-surface-hover-dark text-slate-500 dark:text-slate-400',
    us: 'border-border-subtle dark:border-border-subtle-dark bg-slate-50 dark:bg-surface-hover-dark text-slate-500 dark:text-slate-400',
};

const RegionChip: React.FC = () => {
    const { regions, currentRegion, setRegion, isMultiRegion } = useRegion();
    const { user } = useAuth();

    // Server-reported region wins over the frontend's build-time default
    // — that's what makes `dev` show up as DEV on the homelab even though
    // RegionContext has no entry for it.
    const serverRegion = (user?.region || '').trim().toLowerCase();
    const displayCode = (serverRegion || currentRegion.code).toUpperCase();
    const badgeStyle = STATIC_BADGE_STYLES[serverRegion || currentRegion.code] ?? STATIC_BADGE_STYLES.eu;

    if (!isMultiRegion) {
        return (
            <span
                title="Región actual"
                className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap min-w-[2.5rem] rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badgeStyle}`}
            >
                {displayCode}
            </span>
        );
    }

    return (
        <DropdownSelect
            icon="public"
            value={currentRegion.code}
            onChange={(code) => setRegion(code as typeof currentRegion.code)}
            options={regions.map((r) => ({ value: r.code, label: r.label, sublabel: r.code.toUpperCase() }))}
            variant="field"
            surface="transparent"
            dropdownWidth="w-56"
            className="text-xs"
        />
    );
};

export default RegionChip;
